/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from "react";
import { Avatar, Button, Card, Empty, Input, Modal, Segmented, Select, Space, Tag, Tooltip } from "antd";
import { getParametre } from "../../../../../hooks/useHooks";
import { PARAMETERS } from "../../../../../variables/constants";
import { EditOutlined, PlusOutlined, CreditCardOutlined, PrinterOutlined, CalendarOutlined, DollarOutlined, FileTextOutlined, UserOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import AjouterPriseEnCharge from "../../../components/AjouterPriseEnCharge";
import { frenchDate } from "../../../../../variables/functions";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../../variables/numbersFunctions";
import PriseEnCharge from "../../../docs/PriseEnCharge";
import PriseEnChargeMateriel from "../../../docs/PriseEnChargeMateriel";
import DocumentsAReclamerModal from "../modals/DocumentsAReclamerModal";
import { rightsService } from "../../../../../services/rightsService";

const PrisesEnChargeTab = ({ sinistre, setEditMode, editMode, setRefetch }) => {
    const readOnly = !!sinistre?.lectureSeule;
    const [isPECVisible, setIsPECVisible] = useState(false);
    const [isDocVisible, setIsDocVisible] = useState(false);
    // Format d'aperçu/impression : « classique » (existant) ou « matériel » (nouveau, compact).
    const [docFormat, setDocFormat] = useState("materiel");
    const [priseEnCharge, setPriseEnCharge] = useState(null);
    const [priseEnCharges, setPriseEnCharges] = useState([]);
    // Modale « Documents à réclamer » (bloc « Vous voudrez bien nous adresser » de l'attestation médicale).
    const [docsModalOpen, setDocsModalOpen] = useState(false);
    const [pecForDocs, setPecForDocs] = useState(null);
    // Reste disponible = SAP − paiements en attente (validation ou chèque) : grise
    // « Initialiser le règlement » quand le montant de la PEC dépasse.
    const [resteDisponible, setResteDisponible] = useState(null);
    // Transformation PEC → règlement : motif OBLIGATOIRE (Select des motifs existants + saisie libre).
    const [transformPec, setTransformPec] = useState(null);   // PEC en cours de transformation (modale)
    const [transforming, setTransforming] = useState(false);
    const [motifText, setMotifText] = useState("");           // motif effectif (envoyé au backend)
    const [motifOptions, setMotifOptions] = useState([]);     // motifs MOTIF_REGLEMENT existants
    const primaryColor = '#242166';

    const chargerResteDisponible = () => {
        axiosInstance.get(`/api/decaissement/reste-sap/${sinistre.codeId}`)
            .then((res) => setResteDisponible(Number(res.data?.resteDisponible)))
            .catch(() => setResteDisponible(null));
    };

    // Liste chargée localement : à rappeler après ajout/modification/suppression
    // (l'effet ne se relance pas sur setRefetch parent, le codeId ne change pas).
    const chargerPriseEnCharges = () => {
        axiosInstance
            .get(`/api/prise-en-charge/sinistre/${sinistre.codeId}`)
            .then(({ data }) => {
                setPriseEnCharges(data);
            }).catch((error) => {
                console.error(error);
            });
    };

    useEffect(() => {
        chargerPriseEnCharges();
        chargerResteDisponible();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sinistre.codeId]);

    // Motifs de règlement existants (paramètres MOTIF_REGLEMENT) pour le Select de la transformation.
    useEffect(() => {
        getParametre(PARAMETERS.motifReglement)
            .then(({ data }) => setMotifOptions((data || []).map((m) => ({ label: m.valeur, value: m.valeur }))))
            .catch(() => setMotifOptions([]));
    }, []);

    const hasAssociatedDecaissement = (pec) => {
        if (!pec) {
            return false;
        }

        const { decaissement } = pec;
        if (!decaissement) {
            return false;
        }

        if (Array.isArray(decaissement)) {
            return decaissement.some((entry) => {
                if (!entry || typeof entry !== "object") {
                    return false;
                }
                const candidateKeys = [
                    entry.codeId,
                    entry.id,
                    entry.code,
                    entry.reference,
                    entry.referenceId,
                    entry.numero,
                ];
                return candidateKeys.some((value) => {
                    if (value === null || value === undefined) {
                        return false;
                    }
                    const normalized = `${value}`.trim();
                    return normalized.length > 0;
                });
            });
        }

        if (typeof decaissement === "object") {
            const candidateKeys = [
                decaissement.codeId,
                decaissement.id,
                decaissement.code,
                decaissement.reference,
                decaissement.referenceId,
                decaissement.numero,
            ];
            return candidateKeys.some((value) => {
                if (value === null || value === undefined) {
                    return false;
                }
                const normalized = `${value}`.trim();
                return normalized.length > 0;
            });
        }

        return Boolean(decaissement);
    };

    const handleOpenNewPEC = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setEditMode(false);
        setPriseEnCharge(null);
        setIsPECVisible(true);
    };

    const editPriseEnCharge = (item) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setEditMode(true);
        setPriseEnCharge(item);
        setIsPECVisible(true);
    };

    const previewPriseEnCharge = (item) => {
        setPriseEnCharge(item);
        setIsDocVisible(true);
    };

    // Suppression (soft, historisée). Le backend refuse (409) si la PEC a déjà été
    // transformée en règlement non rejeté.
    const supprimerPriseEnCharge = (pec) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        Modal.confirm({
            title: `Supprimer la prise en charge ${pec.numeroPriseEnCharge} ?`,
            content: "La suppression est historisée. Cette action est définitive pour ce dossier.",
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk: () => {
                return axiosInstance
                    .delete(`/api/sinistre/prise-en-charge/codeId/${sinistre.codeId}/${pec.codeId}`)
                    .then(() => {
                        toast("Prise en charge supprimée.");
                        chargerPriseEnCharges();
                        setRefetch && setRefetch((prev) => !prev);
                    })
                    .catch((e) => {
                        toast(e?.response?.data?.message || "Erreur lors de la suppression de la prise en charge.");
                    });
            },
        });
    };

    // Ouvre la modale de transformation : on EXIGE un motif (Select existant + saisie libre) avant de valider.
    const initDecaissement = (pec) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setTransformPec(pec);
        setMotifText("");
    };

    // Choix d'un motif existant → pré-remplit le champ (qui reste éditable/personnalisable).
    const handleMotifSelect = (value) => setMotifText(value || "");

    const confirmerTransformation = () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        const motif = (motifText || "").trim();
        if (!motif) {
            toast("Veuillez préciser le motif du règlement.");
            return;
        }
        setTransforming(true);
        axiosInstance
            .post(`/api/decaissement/init/prise-en-charge/${transformPec.codeId}/${sinistre.codeId}`, null,
                { params: { motif } })
            .then(() => {
                toast("Décaissement initialisé avec succès!");
                window.location.reload();
            })
            .catch((e) => {
                toast(e?.response?.data?.message || "Erreur lors de l'initialisation");
                setTransforming(false);
            });
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div>
                        <h2 style={{
                            margin: 0,
                            color: primaryColor,
                            fontSize: '24px',
                            fontWeight: '600'
                        }}>
                            Prises en charge / Bons de réparation
                        </h2>
                        <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
                            Gérez les prises en charge et bons de réparation du sinistre
                        </p>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenNewPEC}
                        disabled={sinistre?.sortDossier === "T" || sinistre?.sortDossier === "S" || readOnly}
                        size="large"
                        style={{
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            height: '44px',
                            borderRadius: '8px',
                            fontWeight: '500'
                        }}
                    >
                        Nouvelle prise en charge
                    </Button>
                </div>

                {/* Cards Grid */}
                {priseEnCharges?.length ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                        gap: '20px'
                    }}>
                        {priseEnCharges.map((item, index) => {
                            const isBon = !!item?.bonReparation;
                            return (
                                <Card
                                    key={item.codeId || index}
                                    hoverable
                                    style={{
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        border: '1px solid #e8e8e8',
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ padding: '20px' }}
                                >
                                    {/* Card Header */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px',
                                        paddingBottom: '16px',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <Avatar
                                                size={48}
                                                style={{ backgroundColor: primaryColor }}
                                                icon={<FileTextOutlined />}
                                            />
                                            <div>
                                                <h3 style={{
                                                    margin: 0,
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: primaryColor
                                                }}>
                                                    {isBon ? "Bon de réparation" : "Prise en charge"} N° {index + 1}
                                                </h3>
                                                <Space size={4} wrap style={{ marginTop: '4px' }}>
                                                    <Tag color={isBon ? "volcano" : "blue"} style={{ fontSize: '11px' }}>
                                                        {isBon ? "Bon de réparation" : "Prise en charge"}
                                                    </Tag>
                                                    <Tag color={primaryColor} style={{ fontSize: '12px' }}>
                                                        {item?.numeroPriseEnCharge || "N/A"}
                                                    </Tag>
                                                </Space>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Content */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            marginBottom: '12px'
                                        }}>
                                            <UserOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                    {isBon ? "Bénéficiaire" : "Victime"}
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                    {item?.victime?.personne?.prenom
                                                        ? `${item?.victime?.personne?.nom} ${item?.victime?.personne?.prenom}`
                                                        : item?.victime?.personne?.nom || "-"}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            marginBottom: '12px'
                                        }}>
                                            <FileTextOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                    {isBon ? "Type de réparation" : "Nature de l'acte"}
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                    {item?.natureActe || "-"}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            marginBottom: '12px'
                                        }}>
                                            <DollarOutlined style={{ color: '#52c41a', marginTop: '4px' }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                    Montant
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#52c41a' }}>
                                                    {item?.montant ? displayNumbers(item?.montant) : "-"} FCFA
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '8px',
                                            marginBottom: '12px'
                                        }}>
                                            <CalendarOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                    Délai de validité
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                    {item?.delaiValidite ? frenchDate(item?.delaiValidite) : "-"}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{
                                            fontSize: '12px',
                                            color: '#8c8c8c',
                                            marginTop: '16px',
                                            paddingTop: '12px',
                                            borderTop: '1px solid #f0f0f0'
                                        }}>
                                            Enregistré le : {frenchDate(item?.createdAt)}
                                        </div>
                                    </div>

                                    {/* Card Actions */}
                                    <Space
                                        style={{
                                            width: '100%',
                                            justifyContent: 'center',
                                            flexWrap: 'wrap'
                                        }}
                                        size="small"
                                    >
                                        <Tooltip title="Prévisualiser pour impression">
                                            <Button
                                                icon={<PrinterOutlined />}
                                                onClick={() => previewPriseEnCharge(item)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Imprimer
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="Modifier">
                                            <Button
                                                type="default"
                                                icon={<EditOutlined />}
                                                onClick={() => editPriseEnCharge(item)}
                                                disabled={readOnly}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Modifier
                                            </Button>
                                        </Tooltip>

                                        {/* Documents à réclamer : attestation médicale uniquement (hors bon de réparation). */}
                                        {!item?.bonReparation && (
                                            <Tooltip title="Choisir les documents à réclamer (bloc « Vous voudrez bien nous adresser »)">
                                                <Button
                                                    icon={<FileTextOutlined />}
                                                    onClick={() => { setPecForDocs(item); setDocsModalOpen(true); }}
                                                    disabled={readOnly}
                                                    style={{ borderRadius: '6px' }}
                                                >
                                                    Documents à réclamer
                                                </Button>
                                            </Tooltip>
                                        )}

                                        {!hasAssociatedDecaissement(item) && (() => {
                                            const depasse = resteDisponible != null
                                                && Number(item?.montant || 0) > resteDisponible + 0.01;
                                            return (
                                                <Tooltip title={depasse
                                                    ? `Montant de la prise en charge (${displayNumbers(item?.montant || 0)} F) supérieur au disponible : ${displayNumbers(resteDisponible)} F (SAP − paiements en attente)`
                                                    : "Transformer en décaissement"}>
                                                    <Button
                                                        danger
                                                        icon={<CreditCardOutlined />}
                                                        onClick={() => initDecaissement(item)}
                                                        disabled={depasse || readOnly}
                                                        style={{ borderRadius: '6px' }}
                                                    >
                                                        {depasse ? "Disponible SAP insuffisant" : "Initialiser le règlement"}
                                                    </Button>
                                                </Tooltip>
                                            );
                                        })()}

                                        {!hasAssociatedDecaissement(item) && rightsService("HSI097") && (
                                            <Tooltip title="Supprimer la prise en charge (historisé)">
                                                <Button
                                                    danger
                                                    type="text"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => supprimerPriseEnCharge(item)}
                                                    disabled={readOnly}
                                                    style={{ borderRadius: '6px' }}
                                                >
                                                    Supprimer
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Space>
                                </Card>
                            );
                        })}
                    </div>
                ) : (
                    <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
                        <Empty
                            description={
                                <span style={{ color: '#8c8c8c', fontSize: '16px' }}>
                                    Aucune prise en charge enregistrée sur ce dossier sinistre
                                </span>
                            }
                        />
                    </Card>
                )}

                <AjouterPriseEnCharge
                    open={isPECVisible}
                    priseEnCharge={priseEnCharge}
                    setOpen={setIsPECVisible}
                    sinistre={sinistre}
                    edit={editMode}
                    onSuccess={() => {
                        setIsPECVisible(false);
                        setEditMode(false);
                        setPriseEnCharge(null);
                        chargerPriseEnCharges();
                        setRefetch && setRefetch((prev) => !prev);
                    }}
                    onClose={() => {
                        setEditMode(false);
                        setPriseEnCharge(null);
                    }}
                />
                {/* Modal pour preview la prise en charge */}
                <Modal
                    title={
                        <Space>
                            <span>{priseEnCharge?.bonReparation ? "Bon de réparation" : "Prise en charge"}</span>
                            <Segmented
                                size="small"
                                value={docFormat}
                                onChange={setDocFormat}
                                options={[
                                    { label: "Format matériel", value: "materiel" },
                                    { label: "Format classique", value: "classique" },
                                ]}
                            />
                        </Space>
                    }
                    open={isDocVisible}
                    onCancel={() => setIsDocVisible(false)}
                    footer={null}
                    width={1000}
                    zIndex={2000}
                >
                    {docFormat === "materiel" ? (
                        <PriseEnChargeMateriel
                            priseEnCharge={priseEnCharge}
                            sinistre={sinistre}
                        />
                    ) : (
                        <PriseEnCharge
                            priseEnCharge={priseEnCharge}
                            sinistre={sinistre}
                        />
                    )}
                </Modal>

                {/* Sélection des documents à réclamer (attestation médicale) */}
                <DocumentsAReclamerModal
                    open={docsModalOpen}
                    setOpen={setDocsModalOpen}
                    priseEnCharge={pecForDocs}
                    sinistre={sinistre}
                    onSaved={() => { chargerPriseEnCharges(); }}
                />

                {/* Transformation PEC → règlement : MOTIF OBLIGATOIRE (Select existant + saisie libre) */}
                <Modal
                    open={!!transformPec}
                    title={`Transformer la prise en charge ${transformPec?.numeroPriseEnCharge || ""} en règlement ?`}
                    okText="Oui, je confirme"
                    okType="danger"
                    cancelText="Annuler"
                    confirmLoading={transforming}
                    okButtonProps={{ disabled: !(motifText || "").trim() }}
                    onOk={confirmerTransformation}
                    onCancel={() => { if (!transforming) setTransformPec(null); }}
                    destroyOnClose
                >
                    <p style={{ marginBottom: 16, color: "#6b7280" }}>Toutes vos actions sont historisées.</p>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, marginBottom: 4 }}>Motif du règlement (choisir un motif existant)</div>
                        <Select
                            style={{ width: "100%" }}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Sélectionner un motif existant"
                            options={motifOptions}
                            value={motifOptions.some((o) => o.value === motifText) ? motifText : undefined}
                            onChange={handleMotifSelect}
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                            Motif <span style={{ color: "#dc2626" }}>*</span> (personnalisable)
                        </div>
                        <Input.TextArea
                            rows={3}
                            placeholder="Préciser / personnaliser le motif du règlement"
                            value={motifText}
                            onChange={(e) => setMotifText(e.target.value)}
                        />
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default PrisesEnChargeTab;
